/**
 * The rail: an About card and one card per thing this record is linked to.
 *
 * `<AboutCard>` edits in place rather than opening a modal (§3.4). A modal to
 * change a gate code is a modal somebody opens, reads, and closes without
 * changing anything, because the thing they wanted to check was behind it.
 *
 * `<RelatedRow>` is the other half: a linked record as a line — mark, name,
 * what the link *is*, and, where the link carries notification preferences,
 * the dot grid. The grid is the reason this is not just a list of names: the
 * same person can want texts about visits at their own house and nothing at
 * all about the rental they manage, and that fact lives on the link.
 */
import { useRef, useState, type ReactNode } from 'react'
import { useInRouterContext } from 'react-router-dom'
import { cn } from '../lib/cn.js'
import { CardFocus } from './use-focus-card.js'
import { Button } from '../components/primitives.js'
import {
  IconCall, IconChevronRight, IconEdit, IconEmail, IconPlus, IconSms,
} from '../frame/icons.js'
import { CHANNELS_FOR_EVENT, type NotificationChannel } from '@rowted/shared'

/**
 * **One header, on every card on the record page.**
 *
 * Title, an optional count, a rule under it, and exactly one action on the
 * right. That is the whole vocabulary, and holding to it is the point: a page
 * where About has its header one way, Measurements another and Next up a
 * third is a page somebody has to read rather than scan. Once every card is
 * the same shape, the eye learns it once and the right-hand action is always
 * where the right-hand action is.
 *
 * The rule under the header is what makes them match. The related cards had
 * one — their first row's top border — and the rail's cards did not, so the
 * two columns of the same page were built to two different drawings.
 *
 * `footer` is the other half of the shape: a rule and one control at the
 * bottom. Next up's "See all in Activity" and the contact card's dot legend
 * are the same thing in the same place, and a card that grows one only when
 * it has rows is a card whose height jumps as data arrives.
 */
export function RecordCard({
  title, count, countLabel, action, footer, children, className, focusKey, onFocus,
}: {
  title: string
  count?: number
  /**
   * What goes in the count's slot when the number is not a number.
   *
   * The Set up card's is "· 1 of 6" and carries a `progressbar` role, which
   * is two numbers rather than one. It is the same slot in the same header
   * line, so it is this prop rather than a second header or a word stuffed
   * into the title — those are the two ways a card header stops looking like
   * every other card header.
   */
  countLabel?: ReactNode
  /*
   * `iconAfter` is for the one case where the icon is a direction rather than
   * a verb: "+ Add" leads with its plus, "View all ›" trails its chevron,
   * because the chevron is pointing at where you are about to go.
   */
  action?: { label: string; icon?: ReactNode; iconAfter?: boolean; onClick: () => void }
  /** A rule and one line at the bottom: a legend, a "see all", a note. */
  footer?: ReactNode
  children: ReactNode
  className?: string
  /**
   * What `?focus=<key>` has to say to land on this card — see
   * `use-focus-card.ts`. Two of a property's first steps are fields on cards
   * that are already here rather than records that do not exist yet, so the
   * step points at the card instead of opening a form.
   */
  focusKey?: string
  /** What this card means by *focus*: About opens Edit, Site puts the caret in. */
  onFocus?: () => void
}) {
  const box = useRef<HTMLElement | null>(null)
  const [ringing, setRinging] = useState(false)
  const inRouter = useInRouterContext()

  return (
    <section
      ref={box}
      /* Cleared of the sticky record header, so `nearest` does not land this
         card underneath it. */
      className={cn('overflow-hidden rounded-record border border-line bg-surface',
        'scroll-mt-24 transition-shadow',
        ringing && 'ring-2 ring-accent-ring', className)}
    >
      {/* Nothing, unless this card can be pointed at — `use-focus-card.tsx`
          explains why that is a child rather than a hook up here. */}
      {/*
        * Only inside a router: Next up always carries a key now (`new-request.md`
        * §11.4), and a card drawn bare — a test, a preview — has nothing to read.
        */}
      {focusKey !== undefined && inRouter && (
        <CardFocus
          focusKey={focusKey}
          box={box}
          onRing={setRinging}
          {...(onFocus ? { onFocus } : {})}
        />
      )}
      <header className="flex items-center gap-2 border-b border-line-soft px-[18px] py-2.5">
        <h2 className="m-0 text-[14px] font-semibold text-text">
          {title}
          {count !== undefined && <span className="tnum text-text-muted"> · {count}</span>}
          {countLabel}
        </h2>
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="-mr-1.5 ml-auto inline-flex items-center gap-1 rounded-field px-1.5 py-1
                       text-[12.5px] font-semibold text-accent hover:bg-hover hover:text-accent-dark"
          >
            {!action.iconAfter && action.icon}
            {action.label}
            {action.iconAfter && action.icon}
          </button>
        )}
      </header>

      {children}

      {footer && (
        <div className="border-t border-line-soft px-[18px] py-2 text-[11px] text-text-faint">
          {footer}
        </div>
      )}
    </section>
  )
}

export interface AboutField {
  label: string
  value: ReactNode
  href?: string
  /** Given, the field is editable in place. */
  edit?: {
    value: string
    onChange: (value: string) => void
    placeholder?: string
  }
}

export function AboutCard({
  fields, onSave, onEdit, busy, className, focusKey, onFocus,
}: {
  fields: AboutField[]
  /** Given with editable fields, the card edits in place. */
  onSave?: () => Promise<void> | void
  /**
   * Given instead, Edit opens the record's own form.
   *
   * Most About cards are mostly *derived* — a property's branch, account and
   * customer-since are not free text and a pencil beside them would promise
   * an edit that cannot happen there. Those send Edit to the one form that
   * owns every field, which is where the ⋯ menu sent it anyway.
   */
  onEdit?: () => void
  busy?: boolean
  className?: string
  /** `?focus=about` lands here — `use-focus-card.ts`. */
  focusKey?: string
  /**
   * What About means by *focus*: open the edit form.
   *
   * Passed through rather than assumed, because a card that edits in place
   * (`onSave`) and a card that opens the record's own form (`onEdit`) mean
   * two different things by it, and only the page knows which this one is.
   */
  onFocus?: () => void
}) {
  const [editing, setEditing] = useState(false)
  const editable = fields.some((field) => field.edit)

  const action = onSave && editable && !editing
    ? { label: 'Edit', icon: <IconEdit size={14} />, onClick: () => setEditing(true) }
    : onEdit
      ? { label: 'Edit', icon: <IconEdit size={14} />, onClick: onEdit }
      : undefined

  return (
    <RecordCard
      title="About"
      className={className}
      action={action}
      {...(focusKey ? { focusKey } : {})}
      {...(onFocus ? { onFocus } : {})}
    >
      <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-3.5 gap-y-2.5 px-[18px] pb-4 pt-3">
        {fields.map((field) => (
          <RowPair key={field.label} field={field} editing={editing} />
        ))}
      </div>

      {editing && (
        <footer className="flex items-center justify-end gap-2 border-t border-line-soft px-[18px] py-2.5">
          <Button onClick={() => setEditing(false)}>Cancel</Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={async () => { await onSave?.(); setEditing(false) }}
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </footer>
      )}
    </RecordCard>
  )
}

function RowPair({ field, editing }: { field: AboutField; editing: boolean }) {
  return (
    <>
      <span className="text-body text-text-muted">{field.label}</span>
      {editing && field.edit
        ? (
          <input
            value={field.edit.value}
            placeholder={field.edit.placeholder}
            onChange={(event) => field.edit!.onChange(event.target.value)}
            className="h-8 w-full rounded-field border border-line bg-field px-2 text-body text-text"
          />
        )
        : field.href
          ? (
            <a href={field.href} className="truncate text-body text-accent hover:underline">
              {field.value}
            </a>
          )
          : <span className="truncate text-body text-text">{field.value}</span>}
    </>
  )
}

export function RelatedCard({
  title, count, addLabel, onAdd, children, legend,
}: {
  title: string
  count: number
  addLabel?: string
  onAdd?: () => void
  children: ReactNode
  legend?: ReactNode
}) {
  return (
    <RecordCard
      title={title}
      count={count}
      footer={legend}
      action={onAdd && addLabel
        ? { label: addLabel, icon: <IconPlus size={13} />, onClick: onAdd }
        : undefined}
    >
      {children}
    </RecordCard>
  )
}

export function RelatedRow({
  mark, title, sub, dots, trailing, onOpen,
}: {
  mark: ReactNode
  title: string
  sub?: ReactNode
  /** The notification grid for this link, when the link carries one. */
  dots?: ReactNode
  /** A number the row is about — a measurement's figure, a balance. */
  trailing?: ReactNode
  onOpen: () => void
}) {
  return (
    /*
      * The whole row opens the record, and the dots are the one exception.
      *
      * It was the title alone: the avatar, the role line and the empty space
      * beside them all did nothing, so a card of people had a 180px target in
      * a 340px row and the obvious place to click — the person’s face — was
      * dead. The target is an absolutely positioned button *behind* the
      * content, which is what lets "anywhere on the row" include the gaps
      * without wrapping the dots in a second button or nesting one inside
      * another. The content is `pointer-events-none` so every pixel of it
      * passes the click through, and the dots turn theirs back on.
      */
    <div className="relative flex items-start gap-3 border-line-soft px-[18px] py-2.5
                    [&:not(:first-child)]:border-t hover:bg-hover">
      <button type="button" onClick={onOpen} aria-label={title} className="absolute inset-0" />

      <span className="pointer-events-none relative mt-0.5 shrink-0">{mark}</span>

      <span className="pointer-events-none relative min-w-0 flex-1">
        {/*
          * Three lines, not one.
          *
          * The dots had been beside the name, which on a 340px rail left a
          * property about 100px — "Old Orchard Business Park — clubhouse"
          * became "Old Orchard B…" on every row, and the one thing a list of
          * properties has to do is say which properties they are. Under the
          * sub line they cost 14px and the name gets the whole width.
          */}
        <span className="flex w-full items-center gap-2 text-left">
          <b className="min-w-0 flex-1 truncate text-body font-semibold text-text">{title}</b>
          {trailing && <span className="shrink-0">{trailing}</span>}
          <span className="shrink-0 text-line-strong"><IconChevronRight size={15} /></span>
        </span>
        {sub && <span className="block truncate text-chip text-text-muted">{sub}</span>}
        {/* The one part of the row that is not the link — see below. */}
        {dots && <span className="pointer-events-auto inline-block">{dots}</span>}
      </span>
    </div>
  )
}

/**
 * Call · Text · Email, on a row about a person.
 *
 * The sibling of `<NotificationDots>`, in the same slot and for the opposite
 * question. On a property, a contact row answers "who hears about what", so it
 * carries the grid; on a **job** that is never what anybody wants from it. A
 * job is work at a place on a day, and the question is "ring whoever can let
 * the crew in" — so the row carries the three ways to reach them and nothing
 * about preferences.
 *
 * `tel:`, `sms:` and `mailto:` rather than anything of ours: on a phone they
 * open the dialler, the messages app and mail, which is the whole point, and
 * on a desktop they do whatever that person has set up. A channel the contact
 * has no detail for is **absent**, not greyed — an enabled-looking Call on
 * somebody with no number is a tap that fails, and a row of three where one is
 * dead teaches people not to trust the other two.
 *
 * The contact's `preferredChannel` is the one drawn in the accent colour: it
 * is a fact the contact record already holds, and a crew lead glancing at
 * three identical buttons has no way to know which one gets answered.
 */
export function QuickContacts({
  phone, email, preferred,
}: {
  phone: string | null
  email: string | null
  /** `phone`, `sms`, `email` — whatever the contact said they prefer. */
  preferred?: string | null
}) {
  const ways = [
    phone ? { key: 'phone', label: 'Call', href: `tel:${phone}`, icon: <IconCall size={13} /> } : null,
    phone ? { key: 'sms', label: 'Text', href: `sms:${phone}`, icon: <IconSms size={13} /> } : null,
    email ? { key: 'email', label: 'Email', href: `mailto:${email}`, icon: <IconEmail size={13} /> } : null,
  ].filter((way): way is NonNullable<typeof way> => way !== null)

  if (ways.length === 0) {
    return (
      <span className="mt-1 block text-chip text-text-faint">No phone or email on file</span>
    )
  }

  return (
    <span className="pointer-events-auto mt-1 flex items-center gap-1.5">
      {ways.map((way) => (
        <a
          key={way.key}
          href={way.href}
          /* The row behind it opens the contact; this opens the dialler. */
          onClick={(event) => event.stopPropagation()}
          title={`${way.label} ${phone && way.key !== 'email' ? phone : email ?? ''}`.trim()}
          className={cn(
            'relative flex items-center gap-1 rounded-field border px-1.5 py-0.5',
            'text-chip font-semibold',
            preferred === way.key
              ? 'border-accent-line bg-accent-soft text-accent-dark'
              : 'border-line bg-surface text-text-muted hover:bg-surface-alt',
          )}
        >
          {way.icon}
          {way.label}
        </a>
      ))}
    </span>
  )
}

/**
 * Four events × two channels, as eight dots — §3.4's "dot grid".
 *
 * It replaces a 4×2 grid of checkboxes in the rail, which was accurate and
 * unreadable: eight labelled boxes per person, three people, and the card was
 * taller than the page. The dots say the same thing in one line, and tapping
 * opens the checkboxes to change them.
 *
 * Deliberately *not* inside the row's link, and it stops the click from
 * reaching it — this oval is the only part of the row that does not open
 * the record. A person reaching for a dot is
 * not reaching for the record, and a grid that navigated away on a near miss
 * would be the most annoying control in the product.
 */
export const NOTIFICATION_DOT_EVENTS = [
  { key: 'quotes', letter: 'Q' },
  { key: 'visit_reminders', letter: 'V' },
  { key: 'job_updates', letter: 'U' },
  { key: 'invoices', letter: 'I' },
] as const

/**
 * Which colour a channel's dot is. Post, email, text — the order
 * `CHANNELS_FOR_EVENT` lists them, which is the order the tick boxes are in.
 */
const CHANNEL_TONE: Record<NotificationChannel, string> = {
  // Amber for the post, because it is the slow one and the legend says so.
  mail: 'bg-warn',
  email: 'bg-accent',
  sms: 'bg-info',
}

export function NotificationDots({
  grid, onOpen,
}: {
  /** `{quotes: {email: true, sms: false}, …}` — the effective grid. */
  grid: Record<string, Record<string, boolean | undefined>>
  onOpen: () => void
}) {
  return (
    <button
      type="button"
      /* The row behind it opens the record; this one must not do both. */
      onClick={(event) => { event.stopPropagation(); onOpen() }}
      aria-label="Notification preferences"
      className="relative -ml-1 mt-1 flex items-center gap-2 rounded-field
                 border border-transparent px-1.5 py-0.5
                 hover:border-line hover:bg-surface"
    >
      {NOTIFICATION_DOT_EVENTS.map((event) => {
        const on = grid[event.key] ?? {}
        /*
         * A dot per channel the event *has*, not two every time. Invoices can
         * also go in the post, and drawing two dots for three switches meant
         * a contact set to all three showed the same row as one set to two —
         * the one setting a bookkeeper is most likely to change, invisible.
         */
        return (
          <span key={event.key} className="flex items-center gap-1">
            <span className="text-[10.5px] text-text-faint">{event.letter}</span>
            {CHANNELS_FOR_EVENT[event.key].map((channel) => (
              <Dot key={channel} on={Boolean(on[channel])} tone={CHANNEL_TONE[channel]} />
            ))}
          </span>
        )
      })}
    </button>
  )
}

function Dot({ on, tone }: { on: boolean; tone: string }) {
  return (
    <span aria-hidden className={cn('h-1.5 w-1.5 rounded-full', on ? tone : 'bg-line-strong')} />
  )
}

/** The one-line key under the last row, so the dots are readable cold. */
export function NotificationLegend() {
  return (
    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
      Q quotes · V visits · U updates · I invoices
      <span className="flex items-center gap-1">
        <Dot on tone={CHANNEL_TONE.email} /> email
      </span>
      <span className="flex items-center gap-1">
        <Dot on tone={CHANNEL_TONE.sms} /> text
      </span>
      <span className="flex items-center gap-1">
        <Dot on tone={CHANNEL_TONE.mail} /> post
      </span>
    </span>
  )
}
